/**
 * Header JavaScript - ES6 Class Component
 * Handles mobile hamburger menu, drawer open/close, and icon toggles
 */
(() => {
  'use strict';

  class HeaderComponent {
    constructor() {
      this.drawerElement = null;
      this.pageOverlay = null;
      this.megaMenuDrawer = null;
      this.mobileNavBtn = null;
      this.accordionInitialized = false;
      this.resizeTimeout = null;
      this.scrollTimeout = null;
      this.init();
    }

    init() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          setTimeout(() => this.initializeAll(), 50);
          this.initHeaderHeight();
        });
      } else {
        setTimeout(() => this.initializeAll(), 50);
        this.initHeaderHeight();
      }
    }

    initializeAll() {
      this.initDrawer();
      this.initMobileNavToggle();
      this.initMobileMegaMenu();
      this.initMobileAccordion();
      this.initTransparentHeader();
    }

    // Drawer Management
    initDrawer() {
      this.drawerElement = document.getElementById('header-megamenu-drawer');
      if (!this.drawerElement) {
        setTimeout(() => this.initDrawer(), 100);
        return;
      }

      this.pageOverlay = document.querySelector('.PageOverlay') || this.createPageOverlay();
      this.setupDrawerEvents();
      this.calculateDrawerHeight();
      
      window.addEventListener('resize', () => {
        if (this.drawerElement.getAttribute('aria-hidden') === 'false') {
          this.drawerElement.style.maxHeight = `${window.innerHeight}px`;
          this.calculateDrawerHeight();
        }
      });
    }

    createPageOverlay() {
      const overlay = document.createElement('div');
      overlay.className = 'PageOverlay';
      document.body.appendChild(overlay);
      return overlay;
    }

    openDrawer = (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      if (this.drawerElement.getAttribute('aria-hidden') === 'false') return;

      const searchElement = document.getElementById('Search');
      if (searchElement?.getAttribute('aria-hidden') === 'false') {
        searchElement.setAttribute('aria-hidden', 'true');
      }

      this.drawerElement.setAttribute('aria-hidden', 'false');
      this.drawerElement.style.maxHeight = `${window.innerHeight}px`;
      document.documentElement.classList.add('no-scroll');
      
      if (typeof disableBodyScroll === 'function') {
        disableBodyScroll(true, '[data-scrollable]');
      } else {
        document.body.style.overflow = 'hidden';
      }
      
      this.pageOverlay?.classList.add('is-visible');
      this.drawerElement.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
      this.toggleMobileNavButton();
    }

    closeDrawer = (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      if (this.drawerElement.getAttribute('aria-hidden') === 'true') return;

      this.drawerElement.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('no-scroll');
      
      if (typeof disableBodyScroll === 'function') {
        disableBodyScroll(false, '[data-scrollable]');
      } else {
        document.body.style.overflow = '';
      }
      
      this.pageOverlay?.classList.remove('is-visible');
      this.drawerElement.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
      this.toggleMobileNavButton();
    }

    setupDrawerEvents() {
      document.body.addEventListener('click', (e) => {
        const openBtn = e.target.closest('[data-action="open-drawer"][data-drawer-id="header-megamenu-drawer"]');
        if (openBtn) return this.openDrawer(e);

        const closeBtn = e.target.closest('[data-action="close-drawer"][data-drawer-id="header-megamenu-drawer"]');
        if (closeBtn) return this.closeDrawer(e);
      });

      this.pageOverlay?.addEventListener('click', this.closeDrawer);
      
      document.addEventListener('keyup', (e) => {
        if (e.keyCode === 27 && this.drawerElement.getAttribute('aria-hidden') === 'false') {
          this.closeDrawer(e);
        }
      });
    }

    calculateDrawerHeight() {
      const height = window.innerHeight;
      this.drawerElement.style.top = '0px';
      this.drawerElement.style.height = `${height}px`;
      this.drawerElement.style.maxHeight = `${height}px`;
      
      const drawerContent = this.drawerElement.querySelector('.Drawer__Main');
      if (drawerContent) {
        drawerContent.style.overflowY = drawerContent.scrollHeight <= height ? 'visible' : 'auto';
      }
    }

    // Mobile Nav Button Toggle
    toggleMobileNavButton() {
      this.megaMenuDrawer = document.getElementById('header-megamenu-drawer');
      this.mobileNavBtn = document.querySelector('.mobileNavBtn');
      
      if (!this.megaMenuDrawer || !this.mobileNavBtn) return;

      const isOpen = this.megaMenuDrawer.getAttribute('aria-hidden') === 'false';
      const closeIcons = this.mobileNavBtn.querySelectorAll('.mobileNavIcon--close');
      const hamburgerIcons = this.mobileNavBtn.querySelectorAll('.mobileNavIcon--hamburger');
      const windowWidth = window.innerWidth || document.documentElement.clientWidth;

      if (isOpen) {
        this.mobileNavBtn.classList.add('menu-open');
        this.mobileNavBtn.setAttribute('data-action', 'close-drawer');
        this.mobileNavBtn.setAttribute('data-drawer-id', 'header-megamenu-drawer');
        this.mobileNavBtn.setAttribute('aria-label', 'Close menu');
        this.mobileNavBtn.setAttribute('aria-expanded', 'true');
        
        this.toggleIcons(closeIcons, hamburgerIcons, windowWidth, true);
      } else {
        this.mobileNavBtn.classList.remove('menu-open');
        this.mobileNavBtn.setAttribute('data-action', 'open-drawer');
        this.mobileNavBtn.setAttribute('data-drawer-id', 'header-megamenu-drawer');
        this.mobileNavBtn.setAttribute('aria-label', 'Open menu');
        this.mobileNavBtn.setAttribute('aria-expanded', 'false');
        
        this.toggleIcons(closeIcons, hamburgerIcons, windowWidth, false);
      }
    }

    toggleIcons(closeIcons, hamburgerIcons, windowWidth, isOpen) {
      if (isOpen) {
        closeIcons.forEach(icon => {
          const isMobile = icon.classList.contains('hidden-tablet-and-up');
          const isTablet = icon.classList.contains('hidden-phone');
          const shouldShow = (windowWidth <= 1239 && isMobile) || (windowWidth >= 1240 && isTablet);
          
          if (shouldShow) {
            Object.assign(icon.style, { display: 'block', visibility: 'visible', opacity: '1' });
          } else {
            icon.style.display = 'none';
          }
        });
        hamburgerIcons.forEach(icon => icon.style.display = 'none');
      } else {
        closeIcons.forEach(icon => {
          Object.assign(icon.style, { display: 'none', visibility: 'hidden', opacity: '0' });
        });
        hamburgerIcons.forEach(icon => {
          ['display', 'visibility', 'opacity'].forEach(prop => icon.style.removeProperty(prop));
          const isMobile = icon.classList.contains('hidden-tablet-and-up');
          const isTablet = icon.classList.contains('hidden-phone');
          if ((windowWidth <= 1239 && isTablet) || (windowWidth >= 1240 && isMobile)) {
            icon.style.display = 'none';
          }
        });
      }
    }

    initMobileNavToggle() {
      this.megaMenuDrawer = document.getElementById('header-megamenu-drawer');
      this.mobileNavBtn = document.querySelector('.mobileNavBtn');
      
      if (!this.megaMenuDrawer || !this.mobileNavBtn) {
        setTimeout(() => this.initMobileNavToggle(), 100);
        return;
      }

      this.toggleMobileNavButton();

      const observer = new MutationObserver(() => {
        setTimeout(() => this.toggleMobileNavButton(), 10);
      });

      observer.observe(this.megaMenuDrawer, {
        attributes: true,
        attributeFilter: ['aria-hidden']
      });

      this.mobileNavBtn.addEventListener('click', () => {
        if (!this.mobileNavBtn.getAttribute('data-drawer-id')) {
          this.mobileNavBtn.setAttribute('data-drawer-id', 'header-megamenu-drawer');
        }
        setTimeout(() => this.toggleMobileNavButton(), 100);
      });

      ['drawer:open', 'drawer:close'].forEach(event => {
        this.megaMenuDrawer.addEventListener(event, () => {
          setTimeout(() => this.toggleMobileNavButton(), 50);
        });
      });

      window.addEventListener('resize', () => {
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => this.toggleMobileNavButton(), 150);
      });
    }

    // Mobile Mega Menu Navigation
    initMobileMegaMenu() {
      const drawerElement = document.getElementById('header-megamenu-drawer');
      if (!drawerElement) return;

      const topList = drawerElement.querySelector('.HeaderMegaMenuDrawer__TopList');
      const panelsContainer = drawerElement.querySelector('.HeaderMegaMenuDrawer__Panels');
      const drawerContainer = drawerElement.querySelector('.Drawer__Container');
      if (!topList || !panelsContainer) return;

      const panels = panelsContainer.querySelectorAll('.HeaderMegaMenuDrawer__Panel');
      const megaTypeClassPrefix = 'Drawer__Container--active-';
      const clearMegaTypeState = () => {
        if (!drawerContainer) return;
        drawerContainer.setAttribute('data-active-mega-type', '');
        [...drawerContainer.classList]
          .filter(className => className.startsWith(megaTypeClassPrefix))
          .forEach(className => drawerContainer.classList.remove(className));
      };

      const showTopLevel = () => {
        drawerElement.classList.remove('HeaderMegaMenuDrawer--show-panel');
        drawerContainer?.classList.remove('Drawer__Container--no-padding');
        clearMegaTypeState();
        panels.forEach(panel => {
          panel.classList.remove('is-active');
          panel.setAttribute('aria-hidden', 'true');
          panel.querySelectorAll('.MegaMenu').forEach(mega => {
            mega.setAttribute('aria-hidden', 'true');
          });
        });
      };

      const showPanel = (handle) => {
        drawerElement.classList.add('HeaderMegaMenuDrawer--show-panel');
        panels.forEach(panel => {
          const isTarget = panel.getAttribute('data-mega-panel') === handle;
          panel.classList.toggle('is-active', isTarget);
          panel.setAttribute('aria-hidden', isTarget ? 'false' : 'true');
          panel.querySelectorAll('.MegaMenu').forEach(mega => {
            mega.setAttribute('aria-hidden', isTarget ? 'false' : 'true');
          });

          if (drawerContainer && isTarget) {
            const megaType = panel.getAttribute('data-mega-type');
            const noPaddingTypes = ['mega_menu_type_1', 'mega_menu_type_2', 'mega_menu_type_4'];
            drawerContainer.classList.toggle('Drawer__Container--no-padding', noPaddingTypes.includes(megaType));
            clearMegaTypeState();
            drawerContainer.setAttribute('data-active-mega-type', megaType || '');
            if (megaType) {
              drawerContainer.classList.add(`${megaTypeClassPrefix}${megaType}`);
            }
          }
        });
      };

      topList.addEventListener('click', (e) => {
        const button = e.target.closest('.HeaderMegaMenuDrawer__TopItem[data-mega-target]');
        if (!button) return;
        e.preventDefault();
        const handle = button.getAttribute('data-mega-target');
        if (handle) showPanel(handle);
      });

      panelsContainer.addEventListener('click', (e) => {
        const backButton = e.target.closest('.HeaderMegaMenuDrawer__Back');
        if (!backButton) return;
        e.preventDefault();
        showTopLevel();
      });

      document.body.addEventListener('click', (e) => {
        if (e.target.closest('[data-action="open-drawer"][data-drawer-id="header-megamenu-drawer"]')) {
          setTimeout(showTopLevel, 50);
        }
      });

      showTopLevel();
    }

    // Mobile Accordion
    initMobileAccordion() {
      if (this.accordionInitialized) return;
      
      const drawerElement = document.getElementById('header-megamenu-drawer');
      if (!drawerElement) {
        setTimeout(() => this.initMobileAccordion(), 100);
        return;
      }
      
      const icons = {
        plus: '<svg width="9" height="9" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3.41977 8.84668V8.80957e-05H5.42687V8.84668H3.41977ZM2.77311e-05 5.42694V3.41983H8.84662V5.42694H2.77311e-05Z" fill="black"/></svg>',
        minus: '<svg width="9" height="2" viewBox="0 0 9 2" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0 2.0071V0H8.84659V2.0071H0Z" fill="black"/></svg>'
      };

      drawerElement.addEventListener('click', (e) => {
        const header = e.target.closest('.MegaMenuType1__MobileAccordionHeader');
        if (!header) return;
        
        e.preventDefault();
        e.stopPropagation();
        
        const accordion = header.closest('.MegaMenuType1__MobileAccordion');
        const content = accordion?.querySelector('.MegaMenuType1__MobileAccordionContent');
        const icon = accordion?.querySelector('.MegaMenuType1__MobileAccordionIcon');
        if (!accordion || !content || !icon) return;
        
        const isOpen = accordion.classList.contains('MegaMenuType1__MobileAccordion--open');
        
        if (isOpen) {
          accordion.classList.remove('MegaMenuType1__MobileAccordion--open');
          content.style.display = 'none';
          icon.innerHTML = icons.plus;
          header.setAttribute('aria-expanded', 'false');
        } else {
          accordion.classList.add('MegaMenuType1__MobileAccordion--open');
          content.style.display = 'block';
          icon.innerHTML = icons.minus;
          header.setAttribute('aria-expanded', 'true');
        }
      });
      
      this.accordionInitialized = true;
    }

    // Transparent Header
    initTransparentHeader() {
      const header = document.querySelector('.Header--transparent');
      if (!header) return;
      
      if (header.classList.contains('Header--transparent')) {
        header.setAttribute('data-should-be-transparent', 'true');
      }
      
      const checkTransparentHeader = () => {
        const collectionPage = document.querySelector('.template-collection');
        if (collectionPage) {
          header.classList.remove('Header--transparent');
          return;
        }
        const isCollectionPage = document.body.classList.contains('template-collection');
        const scrollThreshold = isCollectionPage ? 0 : 100;
        const scrollPosition = window.pageYOffset || document.documentElement.scrollTop || window.scrollY;
        
        if (scrollPosition > scrollThreshold) {
          header.classList.remove('Header--transparent');
        } else if (header.getAttribute('data-should-be-transparent') === 'true') {
          header.classList.add('Header--transparent');
        }
      };
      
      checkTransparentHeader();
      
      window.addEventListener('scroll', () => {
        clearTimeout(this.scrollTimeout);
        this.scrollTimeout = setTimeout(checkTransparentHeader, 10);
      }, { passive: true });
    }

    // Header Height Component
    initHeaderHeight() {
      class HeaderHeightComponent {
        constructor(sectionId = 'shopify-section-header') {
          this.headerSection = document.getElementById(sectionId);
          if (!this.headerSection) return;
          this.updateHeaderHeightVar = this.updateHeaderHeightVar.bind(this);
          this.init();
        }

        updateHeaderHeightVar() {
          const rect = this.headerSection.getBoundingClientRect();
          document.documentElement.style.setProperty('--header-height', `${rect.height}px`);
        }

        init() {
          this.updateHeaderHeightVar();
          window.addEventListener('resize', this.updateHeaderHeightVar);
        }
      }

      new HeaderHeightComponent();
    }
  }

  new HeaderComponent();
})();
